import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard,
  ShoppingBag,
  History,
  TrendingUp,
  TrendingDown,
  BadgeCheck,
  Link2,
  Users,
  Settings, 
  MessageCircle, 
  LogOut,
  Menu,
  ListChecks,
  Radar,
  ExternalLink,
  CalendarClock,
  X
} from 'lucide-react';
import { authService } from '../services/authService';
import { handoffUrl, userOrigin } from '../config/api';
import LanguageSelector from './LanguageSelector';
import Toast from './Toast';
import { useTranslation } from '../i18n/LocaleContext';
import socketService from '../services/socketService';

const AdminLayout = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [toast, setToast] = useState(null);
  const notificationSoundRef = useRef(null);

  useEffect(() => {
    const notificationSound = new Audio('/images/vip/universfield-message-notification-199577.mp3');
    notificationSound.preload = 'auto';
    notificationSoundRef.current = notificationSound;

    return () => {
      notificationSound.pause();
      notificationSoundRef.current = null;
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    const socket = socketService.connect(token);
    const refreshUnreadCount = () => {
      socketService.getChatUsers((response) => {
        if (response?.success) {
          setUnreadChatCount(response.users.reduce((total, user) => total + Number(user.unread_count || 0), 0));
        }
      });
    };
    const handleNewMessage = (message) => {
      if (message.sender_role !== 'user') return;

      refreshUnreadCount();
      const notificationSound = notificationSoundRef.current;
      if (notificationSound) {
        notificationSound.currentTime = 0;
        notificationSound.play().catch(() => {});
      }
    };

    socket.on('connect', refreshUnreadCount);
    socket.on('new-message', handleNewMessage);
    socket.on('chat-unread-updated', refreshUnreadCount);
    if (socket.connected) refreshUnreadCount();

    return () => {
      socket.off('connect', refreshUnreadCount);
      socket.off('new-message', handleNewMessage);
      socket.off('chat-unread-updated', refreshUnreadCount);
    };
  }, []);

  // Get user info from localStorage
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const userType = user?.userType || user?.role;
  
  console.log('AdminLayout - User:', user);
  console.log('AdminLayout - UserType:', userType);

  // Menu items for referral admin (only users and chat)
  const referralAdminMenuItems = [
    { path: '/admin/users', label: t('aLayManageUsers'), icon: Users },
    { path: '/admin/chat', label: t('aLaySupport'), icon: MessageCircle, badge: unreadChatCount },
  ];

  // Full menu items for super admin
  const adminMenuItems = [
    { path: '/admin/dashboard', label: t('dashboard'), icon: LayoutDashboard },
    { path: '/admin/orders', label: t('orders'), icon: ShoppingBag },
    { path: '/admin/order-history', label: t('orderHistory'), icon: History },
    { path: '/admin/tasks', label: t('adminTasksNav'), icon: ListChecks },
    { path: '/admin/collect', label: t('adminCollectNav'), icon: Radar },
    { path: '/admin/deposits', label: t('deposits'), icon: TrendingUp },
    { path: '/admin/withdrawals', label: t('withdrawals'), icon: TrendingDown },
    { path: '/admin/kyc', label: t('kycTitle'), icon: BadgeCheck },
    { path: '/admin/social', label: t('aSocialTitle'), icon: Link2 },
    { path: '/admin/users', label: t('users'), icon: Users },
    { path: '/admin/newbie', label: t('aNewbieTitle'), icon: CalendarClock },
    { path: '/admin/chat', label: t('chatSupport'), icon: MessageCircle, badge: unreadChatCount },
    { path: '/admin/settings', label: t('settings'), icon: Settings }
  ];

  // Choose menu based on userType
  // 按明确角色取菜单，缺 user 时不再默认展示全量超管菜单
  const menuItems = userType === 'referral_admin' ? referralAdminMenuItems : adminMenuItems;

  const handleLogout = () => {
    authService.logout();
    setToast({ message: t('logoutSuccess'), type: 'success' });
    setTimeout(() => {
      window.location.href = '/'; // Force reload để App.js check lại auth
    }, 1500);
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={`fixed lg:sticky inset-y-0 left-0 z-50 w-64 h-screen bg-gray-800 text-white transform transition-transform duration-300 lg:translate-x-0 flex flex-col ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="p-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-white">{t('adminPanel')}</h2>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6">
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-brand-500 text-white font-semibold' 
                      : 'text-gray-300 hover:bg-white hover:bg-opacity-10'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="flex-1">{item.label}</span>
                  {item.badge > 0 && (
                    <span className={`min-w-5 h-5 px-1 rounded-full text-xs flex items-center justify-center ${
                      isActive ? 'bg-white text-brand-700' : 'bg-red-500 text-white'
                    }`}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          <a
            href={handoffUrl(userOrigin())}
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-white hover:bg-opacity-10 transition-colors w-full mt-8"
          >
            <ExternalLink className="w-5 h-5" />
            <span>{t('aAuthBackToSite')}</span>
          </a>

          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-red-500 hover:text-white transition-colors w-full mb-6"
          >
            <LogOut className="w-5 h-5" />
            <span>{t('logout')}</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-gray-900 min-w-0 flex flex-col h-screen overflow-hidden">
        {/* Top Bar */}
        <div className="bg-gray-800 shadow-sm px-4 sm:px-6 py-4 flex items-center justify-between border-b border-gray-700">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2 sm:gap-4 ml-auto">
            <LanguageSelector className="text-gray-300" />
            <div className="text-right hidden sm:block">
              <p className="text-sm text-gray-400">{t('welcome')}</p>
              <p className="font-semibold text-white">{t('administrator')}</p>
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-brand-400 to-brand-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold">A</span>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
